import { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import pushNotificationService from '../services/pushNotification';
import apiService from '../services/api';
import { RootState } from '../store';
import { getCurrentAdminInfo } from '../utils/adminUtils';

interface PushNotificationState {
  isSupported: boolean;
  permission: NotificationPermission;
  isSubscribed: boolean;
  isLoading: boolean;
  error: string | null;
}

export const usePushNotifications = (isAuthenticated: boolean) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const initializationAttempted = useRef(false);
  const [state, setState] = useState<PushNotificationState>({
    isSupported: pushNotificationService.isSupported(),
    permission: pushNotificationService.getPermissionStatus(),
    isSubscribed: false,
    isLoading: false,
    error: null,
  });

  // Initialize push notifications
  const initializePushNotifications = useCallback(async () => {
    if (!isAuthenticated || !state.isSupported || initializationAttempted.current) {
      return;
    }

    initializationAttempted.current = true;

    // Get admin info
    const adminInfo = getCurrentAdminInfo();
    const userId = user?.id || adminInfo?.id;

    if (!userId) {
      console.error('No user ID available for push subscription');
      initializationAttempted.current = false;
      return;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await pushNotificationService.initialize();

      if (result.success && result.subscription) {
        // Send subscription to backend
        try {
          const subscriptionData = result.subscription.toJSON();
          await apiService.subscribeToPushNotifications(userId, subscriptionData);
          
          setState((prev) => ({
            ...prev,
            permission: result.permission,
            isSubscribed: true,
            isLoading: false,
          }));

          toast.success('Push notifications enabled! You will receive notifications even when the app is in the background.', {
            autoClose: 5000,
          });
        } catch (error) {
          console.error('Failed to send subscription to server:', error);
          setState((prev) => ({
            ...prev,
            permission: result.permission,
            isSubscribed: false,
            isLoading: false,
            error: 'Failed to register with server',
          }));
          
          toast.warning('Notifications enabled locally, but server registration failed. You may not receive all notifications.', {
            autoClose: 5000,
          });
        }
      } else {
        setState((prev) => ({
          ...prev,
          permission: result.permission,
          isSubscribed: false,
          isLoading: false,
          error: result.permission === 'denied' ? 'Permission denied' : 'Failed to subscribe',
        }));

        if (result.permission === 'denied') {
          toast.error('Push notifications blocked. Please enable them in your browser settings.', {
            autoClose: 7000,
          });
        }
      }
    } catch (error) {
      console.error('Failed to initialize push notifications:', error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: 'Initialization failed',
      }));
    }
  }, [isAuthenticated, state.isSupported, user]);

  // Request permission manually
  const requestPermission = useCallback(async () => {
    if (!state.isSupported) {
      toast.error('Push notifications are not supported in your browser');
      return;
    }

    await initializePushNotifications();
  }, [state.isSupported, initializePushNotifications]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const subscription = await pushNotificationService.getSubscription();
      const endpoint = subscription?.endpoint;

      const success = await pushNotificationService.unsubscribe();
      
      if (success && endpoint) {
        // Notify backend
        try {
          await apiService.unsubscribeFromPushNotifications(endpoint);
        } catch (error) {
          console.error('Failed to unsubscribe from server:', error);
        }

        setState((prev) => ({
          ...prev,
          isSubscribed: false,
          isLoading: false,
        }));

        toast.info('Push notifications disabled');
      } else {
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    } catch (error) {
      console.error('Failed to unsubscribe:', error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: 'Failed to unsubscribe',
      }));
    }
  }, []);

  // Check if already subscribed on mount
  useEffect(() => {
    const checkSubscription = async () => {
      if (!isAuthenticated || !state.isSupported) {
        return;
      }

      const subscription = await pushNotificationService.getSubscription();
      setState((prev) => ({
        ...prev,
        isSubscribed: subscription !== null,
        permission: pushNotificationService.getPermissionStatus(),
      }));
    };

    checkSubscription();
  }, [isAuthenticated, state.isSupported]);

  // Reset initialization flag when auth state changes
  useEffect(() => {
    if (!isAuthenticated) {
      initializationAttempted.current = false;
    }
  }, [isAuthenticated]);

  // Auto-initialize: request permission on first login or subscribe if already granted
  useEffect(() => {
    if (
      isAuthenticated &&
      state.isSupported &&
      !state.isSubscribed &&
      !state.isLoading
    ) {
      // If permission is default (not asked yet) or granted, try to initialize
      if (state.permission === 'default' || state.permission === 'granted') {
        // Small delay to ensure user sees the app first
        const timer = setTimeout(() => {
          initializePushNotifications();
        }, 2000); // 2 second delay after login
        
        return () => clearTimeout(timer);
      }
    }
  }, [isAuthenticated, state.isSupported, state.permission, state.isSubscribed, state.isLoading, initializePushNotifications]);

  return {
    ...state,
    requestPermission,
    unsubscribe,
  };
};

